import type {
  AttributeDto,
  CatalogFacets,
  CatalogQuery,
  CategoryDto,
  ProductCard,
  ProductDetail,
  Suggestion,
} from '@sbg/shared/schemas';
import { apiFetch, apiRequest } from '@/lib/api/client';
import type { QueryParams } from '@/lib/api/client';

/** Filters and sort of the shop listing — a `CatalogQuery` without the paging fields. */
export type CatalogFilterState = Omit<CatalogQuery, 'limit' | 'cursor'>;

/** Products per request (the API default, and what a 4-column grid fills evenly). */
export const PAGE_SIZE = 24;

export interface ProductPage {
  items: ProductCard[];
  /** Opaque keyset cursor for the next page, `null` on the last one. */
  nextCursor: string | null;
  /** Number of products matching the filters (across all pages). */
  total: number;
}

const LIST_KEYS = [
  'color',
  'finish',
  'origin',
  'application',
  'thickness',
  'availability',
] as const;

/**
 * The query string the API expects: lists become repeated parameters, prices are **rupees** (as in
 * the page URL), `sort=featured` and empty values are left out.
 */
export function toApiQuery(
  filters: Partial<CatalogFilterState>,
  paging: { limit?: number; cursor?: string | undefined } = {},
): QueryParams {
  const query: QueryParams = {};
  if (filters.q) query['q'] = filters.q;
  if (filters.category) query['category'] = filters.category;
  for (const key of LIST_KEYS) {
    const values = filters[key];
    if (values && values.length > 0) query[key] = [...values];
  }
  if (filters.priceMin !== undefined) query['priceMin'] = filters.priceMin;
  if (filters.priceMax !== undefined) query['priceMax'] = filters.priceMax;
  if (filters.featured !== undefined) query['featured'] = filters.featured;
  if (filters.sort && filters.sort !== 'featured') query['sort'] = filters.sort;
  if (paging.limit !== undefined) query['limit'] = paging.limit;
  if (paging.cursor) query['cursor'] = paging.cursor;
  return query;
}

export async function fetchProductPage(
  filters: Partial<CatalogFilterState>,
  cursor: string | undefined,
  signal?: AbortSignal,
  limit: number = PAGE_SIZE,
): Promise<ProductPage> {
  const { data, meta } = await apiRequest<ProductCard[]>('/catalog/products', {
    query: toApiQuery(filters, { limit, cursor }),
    signal,
  });
  return { items: data, nextCursor: meta?.nextCursor ?? null, total: meta?.total ?? data.length };
}

/** Counts for every filter value, given the *other* active filters. */
export function fetchFacets(
  filters: Partial<CatalogFilterState>,
  signal?: AbortSignal,
): Promise<CatalogFacets> {
  const { sort: _sort, ...rest } = filters;
  return apiFetch<CatalogFacets>('/catalog/facets', { query: toApiQuery(rest), signal });
}

export const fetchProduct = (slug: string, signal?: AbortSignal): Promise<ProductDetail> =>
  apiFetch<ProductDetail>(`/catalog/products/${encodeURIComponent(slug)}`, { signal });

export const fetchRelated = (slug: string, signal?: AbortSignal): Promise<ProductCard[]> =>
  apiFetch<ProductCard[]>(`/catalog/products/${encodeURIComponent(slug)}/related`, { signal });

export const fetchSuggestions = (q: string, signal?: AbortSignal): Promise<Suggestion[]> =>
  apiFetch<Suggestion[]>('/catalog/suggest', { query: { q }, signal });

export const fetchCategories = (signal?: AbortSignal): Promise<CategoryDto[]> =>
  apiFetch<CategoryDto[]>('/catalog/categories', { signal });

export const fetchAttributes = (signal?: AbortSignal): Promise<AttributeDto[]> =>
  apiFetch<AttributeDto[]>('/catalog/attributes', { signal });
